import React from "react";
import PropTypes from "prop-types";
import { HiArrowDownTray, HiEye, HiArrowPath } from "react-icons/hi2";
import CustomBadge from "../../../ui/CustomBadge/CustomBadge";
import CustomSkeletonTable from "../../../ui/CustomSkeletonTable/CustomSkeletonTable";
import BaseTable from "../../../ui/CustomTanStackTable/CustomTanStackTable";
import TableRowActionsMenu from "../../../ui/TableRowActionsMenu/TableRowActionsMenu";
import useColumnVisibilityStore, {
  selectVisibleColumns,
} from "../../../../stores/columnVisibilityStore";
import {
  BULK_UPLOAD_STATUS_MAP,
  BULK_UPLOAD_STATUS_LABEL,
} from "../../constants/bulkUploadsConstants";
import { formatKES, getDisplayValue } from "../../../../utils/transactionutils";

const OPTIONAL_COLUMN_KEYS = [
  "recipientCount",
  "amount",
  "charges",
  "totalAmount",
];

function buildColumns({ onViewDetails, onResume, isSuperAdmin }) {
  return [
    {
      accessorKey: "batchId",
      header: "Batch ID",
      cell: (info) => (
        <button
          className="text-primary-500 hover:underline font-medium cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails?.(info.row.original);
          }}
        >
          {getDisplayValue(info.getValue())}
        </button>
      ),
      enableSorting: false,
    },
    ...(isSuperAdmin
      ? [
          {
            id: "merchantName",
            header: "Merchant",
            accessorFn: (row) => row.merchant?.merchantName ?? "-",
            cell: (info) => getDisplayValue(info.getValue()),
            enableSorting: false,
          },
        ]
      : []),
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue();
        return (
          <CustomBadge
            color={BULK_UPLOAD_STATUS_MAP[status]}
            variant="light"
            size="sm"
          >
            {BULK_UPLOAD_STATUS_LABEL[status] ?? getDisplayValue(status)}
          </CustomBadge>
        );
      },
      size: 120,
      minSize: 100,
    },
    {
      accessorKey: "paymentChannel",
      header: "Channel",
      cell: (info) => getDisplayValue(info.getValue()),
      enableSorting: false,
    },
    {
      accessorKey: "recipientCount",
      header: "Recipients",
      cell: (info) => getDisplayValue(info.getValue()),
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: (info) => formatKES(info.getValue()),
    },
    {
      accessorKey: "charges",
      header: "Charges",
      cell: (info) => formatKES(info.getValue()),
    },
    {
      accessorKey: "totalAmount",
      header: "Total",
      cell: (info) => formatKES(info.getValue()),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (info) => {
        const row = info.row.original;
        return (
          <TableRowActionsMenu
            items={[
              {
                label: "View Details",
                icon: <HiEye size={14} />,
                onClick: () => onViewDetails?.(row),
              },
              ...(!isSuperAdmin &&
              (row.status === "PENDING_VALIDATION_ERRORS" ||
                row.status === "PENDING_APPROVAL")
                ? [
                    {
                      label: "Resume Batch",
                      icon: <HiArrowPath size={14} />,
                      onClick: () => onResume?.(row),
                    },
                  ]
                : []),
              {
                label: "Download Report",
                icon: <HiArrowDownTray size={14} />,
                disabled: true,
                disabledReason: "Coming soon",
              },
            ]}
          />
        );
      },
      enableSorting: false,
      size: 40,
    },
  ];
}

function BulkUploadsTable({
  data = [],
  isLoading = false,
  isFetching = false,
  pagination,
  totalPages = 0,
  totalElements = 0,
  onPaginationChange,
  onViewDetails,
  onResume,
  isSuperAdmin = false,
}) {
  const visibleOptionalKeys = useColumnVisibilityStore(
    selectVisibleColumns("bulkUploads"),
  );

  const columns = React.useMemo(
    () => buildColumns({ onViewDetails, onResume, isSuperAdmin }),
    [onViewDetails, onResume, isSuperAdmin],
  );

  const columnVisibility = React.useMemo(() => {
    const visibility = {};
    OPTIONAL_COLUMN_KEYS.forEach((key) => {
      visibility[key] = visibleOptionalKeys.includes(key);
    });
    return visibility;
  }, [visibleOptionalKeys]);

  if (isLoading && data.length === 0) {
    return (
      <CustomSkeletonTable
        columns={[1, 2, 3]}
        rowCount={pagination?.pageSize ?? 10}
      />
    );
  }

  return (
    <BaseTable
      data={data}
      columns={columns}
      columnVisibility={columnVisibility}
      pageSize={pagination?.pageSize}
      totalPages={totalPages}
      totalElements={totalElements}
      pagination={pagination}
      isLoading={isLoading}
      isFetching={isFetching}
      onPaginationChange={onPaginationChange}
      paginationMode="server"
      emptyMessage="No batch uploads found "
      rowClassName={() => "bg-white-50 min-h-[50px]"}
    />
  );
}

BulkUploadsTable.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  isFetching: PropTypes.bool,
  pagination: PropTypes.shape({
    pageIndex: PropTypes.number,
    pageSize: PropTypes.number,
  }),
  totalPages: PropTypes.number,
  totalElements: PropTypes.number,
  onPaginationChange: PropTypes.func,
  onViewDetails: PropTypes.func,
  onResume: PropTypes.func,
  isSuperAdmin: PropTypes.bool,
};

export default BulkUploadsTable;
